import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { getStatusBadgeClass, getStatusLabel } from '@/lib/utils';
import { Plus, Eye, Pencil, Trash2, X } from 'lucide-react';
import { permissions, ROLES } from '@/lib/permissions';
import { getDashboardMetric } from '@shared/dashboard-metrics';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';

const STATUS_LIST = ['DRAFT', 'PENDING_MANAGER_APPROVAL', 'MANAGER_APPROVED', 'REJECTED_BY_MANAGER', 'APPROVED', 'REJECTED_BY_CLUSTER_HEAD'];
const PAGE_SIZE = 20;

export default function ActualStatusListPage() {
  const { hasRole, user } = useAuth();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize filters from URL params so dashboard drill-downs land on a
  // page that actually shows the filtered data.
  const metricKey = searchParams.get('metric') || '';
  const initialMetric = getDashboardMetric(metricKey) || undefined;
  const [records, setRecords] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [projectFilter, setProjectFilter] = useState(searchParams.get('projectId') || 'all');
  const [dateFrom, setDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('dateTo') || '');
  const [metric, setMetric] = useState(initialMetric);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1') || 1);
  const [projects, setProjects] = useState<any[] | null>(null);

  // DRAFT is only meaningful for the creator (Engineer) and Admins. Manager,
  // Cluster Head and Viewer are never given drafts, so the option is omitted.
  const canSeeDrafts =
    hasRole(ROLES.SITE_ENGINEER) || hasRole(ROLES.ORG_ADMIN) || hasRole(ROLES.SUPER_ADMIN);
  const statusOptions = STATUS_LIST.filter(s => canSeeDrafts || s !== 'DRAFT');

  // Admins (tenant org admin + platform SUPER_ADMIN) keep full org-wide access
  // and are never pinned to an assigned project.
  const isFullAccess = hasRole(ROLES.ORG_ADMIN) || hasRole(ROLES.SUPER_ADMIN);

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (recordId: string) => api.delete(`/actual-status/${recordId}`),
    onSuccess: () => {
      setDeleteTargetId(null);
      addToast('Draft deleted successfully.', 'success');
      queryClient.invalidateQueries({ queryKey: ['actual-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setReloadKey(k => k + 1);
    },
    onError: (err: any) => {
      setDeleteTargetId(null);
      addToast(err.message || 'Failed to delete', 'error');
    },
  });

  useEffect(() => {
    api.get<{ data: any[] }>('/projects').then(r => setProjects(r.data || []));
  }, []);

  // For scoped users the /projects endpoint already returns only assigned
  // projects. When exactly one is assigned, pin the filter to it so the list is
  // never confused by the "All Projects" option (a URL projectId always wins).
  const autoSelectRef = useRef(true);
  useEffect(() => {
    if (!autoSelectRef.current) return;
    if (projects === null) return;
    autoSelectRef.current = false;
    if (isFullAccess) return;
    if (searchParams.get('projectId')) return;
    if (projects.length === 1) setProjectFilter(projects[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, isFullAccess]);

  useEffect(() => { loadData(); }, [statusFilter, projectFilter, dateFrom, dateTo, metric?.key, page, reloadKey]);

  const validDateRange = !dateFrom || !dateTo || dateFrom <= dateTo;

  // Old URLs may still carry the removed search/type/skill params. They are
  // ignored (never forwarded to the API) and stripped from the URL whenever a
  // supported filter is updated.
  const stripRemovedParams = () => {
    const next = new URLSearchParams(searchParams);
    for (const key of ['search', 'labourType', 'skillLevel']) next.delete(key);
    setSearchParams(next, { replace: true });
  };

  // Any manual filter change overrides the dashboard snapshot view.
  const applyManualChange = (fn: (v: string) => void) => (value: string) => {
    fn(value);
    setMetric(undefined);
    setPage(1);
    stripRemovedParams();
  };

  const clearMetric = () => {
    setMetric(undefined);
    setStatusFilter('all');
    setPage(1);
    stripRemovedParams();
  };

  async function loadData() {
    setLoading(true);
    setLoadFailed(false);
    try {
      if (!validDateRange) { setRecords([]); setTotal(0); setLoading(false); return; }
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (projectFilter && projectFilter !== 'all') params.set('projectId', projectFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (metric) params.set('metric', metric.key);
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));
      const res = await api.get<{ data: any[]; pagination?: { total: number } }>(`/actual-status?${params.toString()}`);
      setRecords(res.data || []);
      setTotal(res.pagination?.total ?? (res.data ?? []).length);
    } catch (e) {
      console.error(e);
      setRecords([]);
      setTotal(0);
      setLoadFailed(true);
    }
    setLoading(false);
  }

  // Scoped users with no assignments: the record list is empty by design, so
  // surface a clear state instead of an empty table behind the filters.
  if (projects !== null && !isFullAccess && projects.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-heading font-bold text-primary break-words">Departmental Labour -Actual</h1>
        </div>
        <Card>
          <CardContent className="p-10 text-center">
            <p className="text-secondary">No projects assigned. Contact your administrator to get project access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-heading font-bold text-primary break-words">Departmental Labour -Actual</h1>
        {hasRole(ROLES.SITE_ENGINEER) && (
          <Link to="/actuals/new" className="shrink-0">
            <Button className="min-h-[44px] w-full sm:w-auto"><Plus size={16} className="mr-2" />New Departmental Labour -Actual</Button>
          </Link>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Select value={statusFilter} onValueChange={applyManualChange(setStatusFilter)}>
              <SelectTrigger aria-label="Status filter"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map(s => <SelectItem key={s} value={s}>{getStatusLabel(s)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={applyManualChange(setProjectFilter)}>
              <SelectTrigger aria-label="Project filter"><SelectValue placeholder="All Projects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {(projects ?? []).map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setMetric(undefined); setPage(1); stripRemovedParams(); }} aria-label="Start date" />
            <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setMetric(undefined); setPage(1); stripRemovedParams(); }} aria-label="End date" />
          </div>
          {!validDateRange && (
            <p className="text-error text-sm mt-2">Start date cannot be after end date.</p>
          )}
        </CardContent>
      </Card>

      {metric && (
        <div className="flex items-center gap-2 px-1">
          <Badge variant="info">Filtered: {metric.label}</Badge>
          <Button variant="ghost" size="sm" onClick={clearMetric} aria-label="Clear dashboard filter">
            <X size={14} className="mr-1" /> Clear filter
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-3 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 rounded-lg bg-surface-secondary animate-pulse" />
              ))}
            </div>
          ) : loadFailed ? (
            <div className="text-center py-8">
              <p className="text-secondary">Unable to load Departmental Labour -Actual records.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setReloadKey(k => k + 1)}>Retry</Button>
            </div>
          ) : records.length === 0 ? (
            <p className="text-center py-8 text-secondary">No Departmental Labour -Actual records found.</p>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-secondary">Project</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary">Block</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary">Work Date</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary">Created By</th>
                      <th className="text-right py-3 px-4 font-medium text-secondary">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r: any) => (
                      <tr key={r.id} className="border-b hover:bg-hover transition-colors">
                        <td className="py-3 px-4">{r.project_name}</td>
                        <td className="py-3 px-4">{r.block_name || '-'}</td>
                        <td className="py-3 px-4">{r.work_date}</td>
                        <td className="py-3 px-4">
                          <Badge variant={getStatusBadgeClass(r.status)}>{getStatusLabel(r.status)}</Badge>
                        </td>
                        <td className="py-3 px-4">{r.created_by_name}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-1">
                            <Link to={`/actuals/${r.id}`}>
                              <Button variant="ghost" size="sm" title="View Departmental Labour -Actual"><Eye size={14} /></Button>
                            </Link>
                            {permissions.canEditOperationalRecord(user?.roles ?? [], { status: r.status, createdBy: r.created_by }, user?.id) && (
                              <Link to={`/actuals/${r.id}/edit`}>
                                <Button variant="ghost" size="sm" title="Edit Departmental Labour -Actual"><Pencil size={14} /></Button>
                              </Link>
                            )}
                            {permissions.canDeleteOperationalRecord(user?.roles ?? [], { status: r.status, createdBy: r.created_by }, user?.id) && (
                              <Button variant="ghost" size="sm" title="Delete draft" onClick={() => setDeleteTargetId(r.id)} disabled={deleteMutation.isPending}>
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3 p-4">
                {records.map((r: any) => (
                  <div key={r.id} className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-primary break-words">{r.project_name}</p>
                      <Badge variant={getStatusBadgeClass(r.status)}>{getStatusLabel(r.status)}</Badge>
                    </div>
                    <dl className="text-sm space-y-1">
                      <div className="flex justify-between gap-3">
                        <dt className="text-secondary shrink-0">Block</dt>
                        <dd className="text-primary text-right break-words">{r.block_name || '-'}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-secondary shrink-0">Work Date</dt>
                        <dd className="text-primary text-right break-words">{r.work_date}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-secondary shrink-0">Created By</dt>
                        <dd className="text-primary text-right break-words">{r.created_by_name}</dd>
                      </div>
                    </dl>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Link to={`/actuals/${r.id}`}>
                        <Button variant="ghost" size="sm" className="min-h-[44px]" title="View Departmental Labour -Actual"><Eye size={16} className="mr-1" />View</Button>
                      </Link>
                      {permissions.canEditOperationalRecord(user?.roles ?? [], { status: r.status, createdBy: r.created_by }, user?.id) && (
                        <Link to={`/actuals/${r.id}/edit`}>
                          <Button variant="ghost" size="sm" className="min-h-[44px]" title="Edit Departmental Labour -Actual"><Pencil size={16} className="mr-1" />Edit</Button>
                        </Link>
                      )}
                      {permissions.canDeleteOperationalRecord(user?.roles ?? [], { status: r.status, createdBy: r.created_by }, user?.id) && (
                        <Button variant="ghost" size="sm" className="min-h-[44px]" title="Delete draft" onClick={() => setDeleteTargetId(r.id)} disabled={deleteMutation.isPending}>
                          <Trash2 size={16} className="mr-1" />Delete
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </CardContent>
      </Card>

      <AlertDialog open={deleteTargetId !== null} onOpenChange={(open) => { if (!open && !deleteMutation.isPending) setDeleteTargetId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete draft?</AlertDialogTitle>
            <AlertDialogDescription>This Departmental Labour -Actual draft will be permanently deleted. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:pointer-events-none disabled:opacity-50"
              disabled={deleteMutation.isPending}
              onClick={() => { if (deleteTargetId) deleteMutation.mutate(deleteTargetId); }}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Draft'}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

